import { useEffect, useState } from 'react'

import { formatElapsed } from '@/lib/format'
import { StatusDot } from '@/ui/primitives'

import type { TranscriptEntry } from './transcript'

interface Props {
  turn: TranscriptEntry[]
  startedAt: number
  statusText?: string
}

function deriveStatus(turn: TranscriptEntry[]): string {
  for (let i = turn.length - 1; i >= 0; i--) {
    const e = turn[i]
    if (e.kind === 'tool_use') {
      const cmd = (e.command ?? e.description ?? 'command').replace(/\s+/g, ' ')
      const short = cmd.length > 110 ? cmd.slice(0, 107) + '…' : cmd
      return `running: ${short}`
    }
    if (e.kind === 'tool_result') return 'output received — thinking…'
    if (e.kind === 'thinking') return 'thinking…'
    if (e.kind === 'assistant') return 'writing response…'
  }
  return 'working…'
}

export function ActivityCard({ turn, startedAt, statusText }: Props) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 500)
    return () => window.clearInterval(id)
  }, [])

  const status = statusText || deriveStatus(turn)
  const commands = turn.filter((e) => e.kind === 'tool_use').length
  const elapsed = formatElapsed(now - startedAt)

  return (
    <div
      className="border-l-2 border-accent pl-3 font-mono text-[13px] animate-fade-in"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-2">
        <StatusDot tone="accent" pulse size={7} />
        <span className="uppercase tracking-[0.08em] text-[11.5px] text-accent">running</span>
        <span className="text-fg-dim">·</span>
        <span className="text-fg-soft truncate flex-1">{status}</span>
        <span className="text-fg tnum shrink-0">T+{elapsed}</span>
      </div>
      <div className="mt-0.5 text-[11.5px] text-fg-dim">
        commands: {commands}
      </div>
    </div>
  )
}
